import { useEffect, type RefObject } from 'react';

/**
 * While `active` is true, writes the top and the height of the visual
 * viewport to --vv-top and --vv-height on the element. iOS Safari does not
 * resize the layout viewport for the keyboard, so a fixed sheet uses these
 * values to keep its composer above the keyboard (design system README,
 * "On a phone": the sheet follows visualViewport). Pinch zoom also changes
 * the visual viewport, so the values are removed while the page is zoomed.
 */
export function useVisualViewportBox(ref: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    const element = ref.current;
    const viewport = typeof window === 'undefined' ? null : window.visualViewport;
    if (!active || !element || !viewport) return;

    const clear = () => {
      element.style.removeProperty('--vv-top');
      element.style.removeProperty('--vv-height');
    };
    const update = () => {
      if (Math.abs(viewport.scale - 1) > 0.01) {
        clear();
        return;
      }
      element.style.setProperty('--vv-top', `${viewport.offsetTop}px`);
      element.style.setProperty('--vv-height', `${viewport.height}px`);
    };

    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      clear();
    };
  }, [active, ref]);
}
